import {
  AI_PROMPT_SECTION_KEYS,
  aiGenerationSupportsSampling,
  applyAiModelGeneration,
  resolveAiModelGeneration,
} from '@citizen-walk/shared-types';
import type {
  AiGenerationConfig,
  AiOutputFieldDef,
  AiOutputFormatSchema,
  AiPromptInputMapping,
  AiPromptSections,
  AiTitleGenerationConfig,
  AiValidatorRule,
  DistrictAiAnalysisConfig,
  DistrictMode,
  FieldDef,
} from '@citizen-walk/shared-types';

/**
 * AI 解析統合エディタ (AiAnalysisEditorPage、テンプレモード/Phase3) の純関数ヘルパ。
 *
 * UI から切り離してユニットテスト可能にする。型/ランタイムは変更しないため、ここで定義するのは
 * 「admin 編集時の補助計算」のみ (slug 生成 / postFields → inputFieldKeys+mapping 自動生成 /
 * 保存 payload の undefined 除去 / M3 schema dirty 判定)。
 */

/** テンプレ使用地区サマリ (テンプレ一覧の「使用地区」列表示用)。 */
export interface TemplateUsageDistrict {
  id: string;
  name: string;
}

/**
 * 地区一覧を「どの AI 解析設定テンプレ (settingsId) を使っているか」で逆引きする
 * (統合エディタ テンプレ一覧の使用地区表示 + アーカイブ安全判定用)。
 *  - `district.aiAnalysis.settingsId` をキーに地区 {id,name} を束ねる。
 *  - settingsId 未設定 (AI 解析未紐付け) の地区は無視する。
 *  - 戻り値は settingsId → 地区配列。地区の元順 (name 昇順 subscribe) を保持。
 */
export function groupDistrictsByTemplate(
  districts: readonly {
    id: string;
    name: string;
    aiAnalysis?: { settingsId?: string | null } | null;
  }[],
): Record<string, TemplateUsageDistrict[]> {
  const out: Record<string, TemplateUsageDistrict[]> = {};
  for (const d of districts) {
    const sid = d.aiAnalysis?.settingsId;
    if (!sid) continue;
    (out[sid] ??= []).push({ id: d.id, name: d.name });
  }
  return out;
}

// -----------------------------------------------------------------------------------------
// 出力フォーマット (M3) の使用状況 — 「テンプレ経由」と「地区の直接指定」の 2 経路を集計する
// -----------------------------------------------------------------------------------------

/** 出力フォーマット (M3) を参照している AI 解析テンプレ (M1) のサマリ。 */
export interface OutputFormatUsageTemplate {
  id: string;
  displayName: string;
  /** そのテンプレ自体がアーカイブ済か (一覧で注記する)。 */
  archived: boolean;
}

/** 出力フォーマット (M3) を使っている地区と、その参照経路。 */
export interface OutputFormatUsageDistrict {
  id: string;
  name: string;
  /**
   * どの経路でこの出力フォーマットに行き着くか。
   *  - `template`: 地区が使うテンプレ (M1) の outputFormatId から
   *  - `direct`: 地区設定でこの出力フォーマットを直接指定 (outputFormatIdOverride)
   */
  via: 'template' | 'direct';
  /** via='template' のときの経由テンプレ表示名。 */
  templateName?: string;
}

/** 出力フォーマット 1 件あたりの使用状況。 */
export interface OutputFormatUsage {
  templates: OutputFormatUsageTemplate[];
  districts: OutputFormatUsageDistrict[];
}

export interface OutputFormatUsageInput {
  /** AI 解析テンプレ (M1)。**アーカイブ済も含めて**渡すこと (使用の有無を取りこぼさないため)。 */
  templates: readonly {
    id: string;
    displayName: string;
    outputFormatId?: string | null;
    archivedAt?: unknown;
  }[];
  districts: readonly {
    id: string;
    name: string;
    aiAnalysis?: {
      settingsId?: string | null;
      outputFormatIdOverride?: string | null;
    } | null;
  }[];
}

/**
 * 出力フォーマット (M3) を「どのテンプレ / どの地区が使っているか」で逆引きする
 * (M3 一覧の使用状況表示 + アーカイブ安全判定用)。
 *
 * 参照経路は 2 つあり、**両方**を集計する:
 *  1. テンプレ (M1) の `outputFormatId`
 *  2. 地区の `aiAnalysis.outputFormatIdOverride` (直接指定)
 *
 * 地区が直接指定しているときは、テンプレ側の outputFormatId より **優先** される
 * (shared-types DistrictAiAnalysisConfig.outputFormatIdOverride / functions の analyzePost と同じ
 * 解決順)。したがって直接指定のある地区は、テンプレ経由の使用地区としては数えない。
 *
 * 戻り値は 出力フォーマット id → 使用状況。テンプレ / 地区の元順 (displayName / name 昇順
 * subscribe) を保持する。一度も参照されない id はキー自体を持たない (= 未使用)。
 */
export function groupOutputFormatUsage(
  input: OutputFormatUsageInput,
): Record<string, OutputFormatUsage> {
  const out: Record<string, OutputFormatUsage> = {};
  const bucket = (formatId: string): OutputFormatUsage =>
    (out[formatId] ??= { templates: [], districts: [] });

  const templateById = new Map<string, OutputFormatUsageInput['templates'][number]>();
  for (const t of input.templates) {
    templateById.set(t.id, t);
    const formatId = t.outputFormatId?.trim();
    if (!formatId) continue;
    bucket(formatId).templates.push({
      id: t.id,
      displayName: t.displayName,
      archived: Boolean(t.archivedAt),
    });
  }

  for (const d of input.districts) {
    const direct = d.aiAnalysis?.outputFormatIdOverride?.trim();
    if (direct) {
      // 直接指定はテンプレ側の設定より優先されるため、テンプレ経由には数えない。
      bucket(direct).districts.push({ id: d.id, name: d.name, via: 'direct' });
      continue;
    }
    const settingsId = d.aiAnalysis?.settingsId;
    if (!settingsId) continue;
    const template = templateById.get(settingsId);
    const formatId = template?.outputFormatId?.trim();
    if (!template || !formatId) continue;
    bucket(formatId).districts.push({
      id: d.id,
      name: d.name,
      via: 'template',
      templateName: template.displayName,
    });
  }

  return out;
}

/**
 * 表示名から Firestore ドキュメント ID 候補 (kebab-case slug) を生成する (要件4: id 手入力廃止)。
 *  - 英数字はそのまま小文字化、それ以外 (空白・記号・日本語) は区切り `-` に畳む。
 *  - 連続 `-` は 1 個に、先頭/末尾の `-` は除去。
 *  - 結果が空 (日本語のみ等) のときは空文字を返す (呼び出し側で displayName と組み合わせる)。
 */
export function slugify(name: string): string {
  return name
    .trim()
    .toLowerCase()
    .replace(/[^a-z0-9]+/g, '-')
    .replace(/^-+|-+$/g, '');
}

/**
 * 既存 id 集合と衝突しない一意な slug を返す。slug が空なら fallback (`profile`) を起点にする。
 * 衝突時は `-2`, `-3`, ... を付与する。
 */
export function uniqueSlug(name: string, existingIds: readonly string[]): string {
  const base = slugify(name) || 'profile';
  const set = new Set(existingIds);
  if (!set.has(base)) return base;
  for (let i = 2; i < 1000; i += 1) {
    const candidate = `${base}-${i}`;
    if (!set.has(candidate)) return candidate;
  }
  // 実用上ここには到達しないが型安全のため。
  return `${base}-${Date.now()}`;
}

/**
 * ドキュメント ID (Firestore doc id) に許す形 = kebab-case の人間可読 ID
 * (docs/10 §3-3、shared-types MasterCommon.id)。
 * 半角英小文字・数字をハイフン 1 個で区切る。先頭/末尾のハイフン・連続ハイフンは不可。
 */
export const DOCUMENT_ID_PATTERN = /^[a-z0-9]+(-[a-z0-9]+)*$/;

/**
 * 新規作成 / 複製時に管理者が入力したドキュメント ID を検証する (保存前のリアルタイム表示 +
 * handleSave の最終防御で共用)。
 *
 *  - 問題があれば「管理者にそのまま見せる日本語メッセージ」を返す (内部識別子は出さない)。
 *  - 問題なければ null。
 *  - `existingIds` には**アーカイブ済も含めた全 id** を渡すこと。新規保存は merge:false の
 *    全置換なので、アーカイブ済と同 id で保存するとそのテンプレを無警告で上書き消滅させる。
 *  - 前後の空白は無視して判定する (保存側も trim した値を使うこと)。
 *  - `entityLabel` は重複メッセージ内の呼び名 (既定「テンプレート」)。AI 出力フォーマット
 *    マスタ (M3) など、テンプレート以外のマスタから使うときに「出力フォーマット」等を渡す。
 */
export function validateDocumentId(
  id: string,
  existingIds: readonly string[],
  entityLabel = 'テンプレート',
): string | null {
  const value = id.trim();
  if (!value) return 'ドキュメント ID を入力してください';
  if (/[^a-z0-9-]/.test(value)) {
    return 'ドキュメント ID に使えるのは半角の英小文字・数字・ハイフン (-) だけです (例: ecw-yokohama)';
  }
  if (!DOCUMENT_ID_PATTERN.test(value)) {
    return 'ドキュメント ID の先頭と末尾にハイフン (-) は使えません。ハイフンを 2 つ以上続けることもできません';
  }
  if (existingIds.includes(value)) {
    return `同じドキュメント ID の${entityLabel}が既にあります (アーカイブ済みのものを含む)。別の ID を入力してください`;
  }
  return null;
}

/** 「別名で保存」(複製) 時に表示名へ付ける接尾辞。 */
export const DUPLICATE_NAME_SUFFIX = ' のコピー';

/**
 * draft を deep copy する (複製元と参照を共有させないため)。
 * draft は plain object / 配列 / スカラーのみで構成されるため structuredClone で足りる。
 * 非対応 env 用に JSON 経由の fallback を持つ (useSurveyCopyLink.cloneSurveyQuestions と同じ流儀)。
 */
export function deepCloneDraft<T>(value: T): T {
  if (typeof structuredClone === 'function') return structuredClone(value);
  return JSON.parse(JSON.stringify(value)) as T;
}

/**
 * 「別名で保存」用に、編集中テンプレの内容をそのまま引き継いだ新規テンプレ draft を作る。
 *
 *  - 中身 (プロンプト / 参照マスタ / 出力フォーマット / 検証 / パラメータ等) は全て引き継ぐ。
 *    保存前の未確定の編集内容も含めて複製できるよう、Firestore ではなく draft を入力に取る。
 *  - 表示名は `{元の名前} のコピー`。
 *  - id は元 id + `-copy` を起点に uniqueSlug で採番する (衝突時は `-2`, `-3`, ...)。
 *    existingIds には**アーカイブ済も含めた全 id** を渡すこと (新規保存は全置換のため、
 *    アーカイブ済と同 id を採番すると復元不能な上書きになる)。
 *  - 呼び出し側は戻り値を draft に入れて「新規モード」へ切り替えるだけで、保存はしない
 *    (誤操作で意図しないテンプレが増えないよう、確定は「保存」ボタンに委ねる)。
 */
export function duplicateDraft<T extends { id: string; displayName: string }>(
  draft: T,
  existingIds: readonly string[],
): T {
  const copy = deepCloneDraft(draft);
  return {
    ...copy,
    displayName: `${draft.displayName}${DUPLICATE_NAME_SUFFIX}`,
    id: uniqueSlug(`${draft.id}-copy`, existingIds),
  };
}

// -----------------------------------------------------------------------------------------
// モードプロファイル (M10) の使用状況 — 「そのプロファイルを採用した地区」を集計する
// -----------------------------------------------------------------------------------------

/**
 * そのモードプロファイルを採用した地区。
 *
 * 新地区作成時 / 「地区へ反映」時に `District.profileId` + `District.profileVersion` が
 * 記録される (ModeProfileApplyModal)。プロファイルは採用後に更新されうるので、地区が
 * 採用した版 (`appliedVersion`) も保持し、呼出側が現行版との差を出せるようにする。
 */
export interface ModeProfileUsageDistrict {
  id: string;
  name: string;
  mode: DistrictMode;
  modeLabel?: string;
  /** 地区が採用した時点の profileVersion (未記録なら undefined)。 */
  appliedVersion?: string;
}

/** モードプロファイル 1 件あたりの使用状況。 */
export interface ModeProfileUsage {
  districts: ModeProfileUsageDistrict[];
}

export interface ModeProfileUsageInput {
  districts: readonly {
    id: string;
    name: string;
    mode: DistrictMode;
    modeLabel?: string;
    profileId?: string | null;
    profileVersion?: string | null;
  }[];
}

/**
 * モードプロファイル (M10) を「どの地区が採用しているか」で逆引きする
 * (一覧の使用状況表示 + アーカイブ安全判定用)。
 *
 * 一度も採用されていない id はキー自体を持たない (= 未使用)。地区は元順 (name 昇順 subscribe)
 * を保持する。
 */
export function groupModeProfileUsage(
  input: ModeProfileUsageInput,
): Record<string, ModeProfileUsage> {
  const out: Record<string, ModeProfileUsage> = {};
  for (const d of input.districts) {
    const id = d.profileId?.trim();
    if (!id) continue;
    const bucket = (out[id] ??= { districts: [] });
    bucket.districts.push({
      id: d.id,
      name: d.name,
      mode: d.mode,
      modeLabel: d.modeLabel,
      appliedVersion: d.profileVersion?.trim() || undefined,
    });
  }
  return out;
}

// -----------------------------------------------------------------------------------------
// 分析表示設定 (M5) の使用状況 — 「地区の直接紐付け」と「モードプロファイルの推奨設定」の
// 2 経路を集計する
// -----------------------------------------------------------------------------------------

/**
 * M5 `analysisDisplayConfigs` を直接紐付けている地区。
 *
 * M3 と異なり M5 には「テンプレ経由」の参照経路が無く、`District.analysisDisplayConfigId` が
 * 唯一の直接参照。地区の mode と設定の mode が食い違うケース (別モード地区が流用する等) を
 * 見分けられるよう、地区自身の mode / modeLabel も保持する。
 */
export interface DisplayConfigUsageDistrict {
  id: string;
  name: string;
  mode: DistrictMode;
  modeLabel?: string;
}

/**
 * この M5 を「新規地区作成時の既定表示設定」として推奨しているモードプロファイル (M10)。
 * モードプロファイルは M3/M1 で言う「テンプレ」に相当し、新規地区作成時に
 * `recommendedMasters.displayConfigId` が `District.analysisDisplayConfigId` へ転写される。
 */
export interface DisplayConfigUsageModeProfile {
  id: string;
  name: string;
  mode: DistrictMode;
  modeLabel?: string;
  /** そのモードプロファイル自体がアーカイブ済か (一覧で注記する)。 */
  archived: boolean;
}

/** 分析表示設定 (M5) 1 件あたりの使用状況。 */
export interface DisplayConfigUsage {
  districts: DisplayConfigUsageDistrict[];
  modeProfiles: DisplayConfigUsageModeProfile[];
}

export interface DisplayConfigUsageInput {
  districts: readonly {
    id: string;
    name: string;
    mode: DistrictMode;
    modeLabel?: string;
    analysisDisplayConfigId?: string | null;
  }[];
  /** **アーカイブ済も含めて**渡すこと (使用の有無を取りこぼさないため)。 */
  modeProfiles: readonly {
    id: string;
    name: string;
    mode: DistrictMode;
    modeLabel?: string;
    archivedAt?: unknown;
    recommendedMasters?: { displayConfigId?: string | null } | null;
  }[];
}

/**
 * 分析表示設定 (M5) を「どの地区が使っているか / どのモードプロファイルが推奨しているか」で
 * 逆引きする (M5 一覧の使用状況表示 + アーカイブ安全判定用)。
 *
 * 一度も参照されない id はキー自体を持たない (= 未使用)。地区 / モードプロファイルとも
 * 元順 (name 昇順 subscribe) を保持する。
 */
export function groupAnalysisDisplayConfigUsage(
  input: DisplayConfigUsageInput,
): Record<string, DisplayConfigUsage> {
  const out: Record<string, DisplayConfigUsage> = {};
  const bucket = (id: string): DisplayConfigUsage =>
    (out[id] ??= { districts: [], modeProfiles: [] });

  for (const d of input.districts) {
    const id = d.analysisDisplayConfigId?.trim();
    if (!id) continue;
    bucket(id).districts.push({ id: d.id, name: d.name, mode: d.mode, modeLabel: d.modeLabel });
  }

  for (const p of input.modeProfiles) {
    const id = p.recommendedMasters?.displayConfigId?.trim();
    if (!id) continue;
    bucket(id).modeProfiles.push({
      id: p.id,
      name: p.name,
      mode: p.mode,
      modeLabel: p.modeLabel,
      archived: Boolean(p.archivedAt),
    });
  }

  return out;
}

/**
 * postFields のうち「AI に送る投稿データ」として選択候補にすべきものを返す (要件5、old2 踏襲)。
 * 実行時に自動で送る対象 (withPostFieldMappings) と同じく、非表示・写真・位置は除外する。
 */
export function selectablePostFields(postFields: FieldDef[]): FieldDef[] {
  return postFields.filter(
    (f) => f.mode !== 'hidden' && f.type !== 'photo' && f.type !== 'location',
  );
}

/**
 * inputFieldKeys の選択状態 (要件5)。
 *  - undefined: 全 postField を送る既定動作 (= 全選択)
 *  - []: 何も送らない (全解除)
 *  - string[]: その key のみ
 *
 * チェックボックス操作の結果を上記 3 状態へ正規化する。
 * 選択集合が「選択可能フィールド全件」と一致するなら undefined (= 全選択既定) に畳む。
 */
export function normalizeInputFieldKeys(
  selected: readonly string[],
  selectableKeys: readonly string[],
): string[] | undefined {
  const selSet = new Set(selected);
  // 選択可能キーに含まれるものだけを順序保持で残す (古い不明 key を除去)。
  const filtered = selectableKeys.filter((k) => selSet.has(k));
  if (filtered.length === 0) return [];
  if (filtered.length === selectableKeys.length) return undefined; // 全選択 → 既定
  return filtered;
}

/**
 * inputFieldKeys 値 (undefined/[]/string[]) を「チェック済みキー集合」へ展開する。
 * undefined は全選択 (= 全 selectableKeys)、[] は空、配列はその集合 (selectableKeys 内のみ)。
 */
export function expandInputFieldKeys(
  value: string[] | undefined,
  selectableKeys: readonly string[],
): string[] {
  if (value === undefined) return [...selectableKeys];
  const set = new Set(value);
  return selectableKeys.filter((k) => set.has(k));
}

/**
 * 選択中の postField key から promptInputMapping を裏で自動生成する (要件3/5)。
 *  - title / comment は builtin (source=そのままの key) として扱う。
 *  - それ以外は `extraFields.<key>` を source にする。
 *  - templateVar は key と同一。
 *  - 既存 mapping は尊重: 同一 templateVar の行は既存 source を保持。未対応 key を補完するのみ。
 *
 * 返り値は「既存 mapping + 不足分の自動生成行」。既存行の順序を保ち、新規は末尾に追加する。
 */
const BUILTIN_POST_SOURCES = new Set(['title', 'comment']);

export function sourceForPostFieldKey(key: string): string {
  // 実行時 (promptBuilder) は `post.` 起点で引くので、その形で作る (2026-10-08)。
  return BUILTIN_POST_SOURCES.has(key) ? `post.${key}` : `post.extraFields.${key}`;
}

export function autoPromptInputMapping(
  selectedKeys: readonly string[],
  existing: readonly AiPromptInputMapping[],
): AiPromptInputMapping[] {
  const out: AiPromptInputMapping[] = existing.map((m) => ({ ...m }));
  const existingVars = new Set(out.map((m) => m.templateVar));
  for (const key of selectedKeys) {
    if (!key) continue;
    if (existingVars.has(key)) continue;
    out.push({ templateVar: key, source: sourceForPostFieldKey(key) });
    existingVars.add(key);
  }
  return out;
}

/**
 * Firestore は undefined を拒否する (commit 079aa32 と同種の罠)。保存 payload から
 * undefined を再帰的に除去する。null / [] / 0 / '' は保持する (意図された値)。
 * 配列要素の undefined は除去 (詰める)。Date / Timestamp 等の非 plain object はそのまま返す。
 */
export function stripUndefinedDeep<T>(value: T): T {
  if (Array.isArray(value)) {
    return value.filter((v) => v !== undefined).map((v) => stripUndefinedDeep(v)) as unknown as T;
  }
  if (value !== null && typeof value === 'object') {
    // plain object のみ走査 (Date 等のクラスインスタンスはそのまま)。
    const proto = Object.getPrototypeOf(value);
    if (proto === Object.prototype || proto === null) {
      const out: Record<string, unknown> = {};
      for (const [k, v] of Object.entries(value as Record<string, unknown>)) {
        if (v === undefined) continue;
        out[k] = stripUndefinedDeep(v);
      }
      return out as T;
    }
  }
  return value;
}

/**
 * M3 schema が編集により元と変わったか (dirty) を判定する (保存時に M3 を upsert すべきか)。
 * 安定キー順で JSON 化して比較する。undefined はキー自体を省く正規化を行ってから比較する
 * (UI で一時的に undefined を持っても、保存正規化後に同値なら dirty 扱いしない)。
 */
export function isM3SchemaDirty(
  original: AiOutputFormatSchema | undefined,
  edited: AiOutputFormatSchema,
): boolean {
  return stableStringify(original ?? null) !== stableStringify(edited);
}

/** キー順を安定化した JSON 文字列化 (undefined キーは省略、配列順は保持)。 */
export function stableStringify(value: unknown): string {
  return JSON.stringify(sortKeysDeep(stripUndefinedDeep(value)));
}

function sortKeysDeep(value: unknown): unknown {
  if (Array.isArray(value)) return value.map(sortKeysDeep);
  if (value !== null && typeof value === 'object') {
    const proto = Object.getPrototypeOf(value);
    if (proto === Object.prototype || proto === null) {
      const out: Record<string, unknown> = {};
      for (const k of Object.keys(value as Record<string, unknown>).sort()) {
        out[k] = sortKeysDeep((value as Record<string, unknown>)[k]);
      }
      return out;
    }
  }
  return value;
}

// =========================================================================================
// (Phase 4) 地区モード — 継承トグル状態 と override 保存形 (DistrictAiAnalysisConfig) の組み立て
// =========================================================================================
// 地区モードは「テンプレ (M1) を settingsId で共用しつつ各項目を地区単位で差分 override」する。
// 各「項目」(下記 OverrideFieldKey) と各プロンプトセクションの ON/OFF を DistrictOverrideState で
// 保持し、ON のものだけを DistrictAiAnalysisConfig に詰める (OFF はキー自体を書かない=継承へ戻る)。
// 実効値・由来 (template/override) は shared-types の resolveEffectiveAiAnalysis を使う (二重実装回避)。

/** 地区モードでトグル可能な「項目」単位 (promptSections はセクション毎に別管理)。 */
export type OverrideFieldKey =
  | 'outputFormatId'
  | 'inputFieldKeys'
  | 'categoryMasterIds'
  | 'generationConfig'
  | 'titleGeneration'
  | 'validators'
  | 'outputFields';

export const OVERRIDE_FIELD_KEYS: OverrideFieldKey[] = [
  'outputFormatId',
  'inputFieldKeys',
  'categoryMasterIds',
  'generationConfig',
  'titleGeneration',
  'validators',
  'outputFields',
];

/** 各 override 項目 + 各プロンプトセクションの ON/OFF を保持する状態。 */
export interface DistrictOverrideState {
  fields: Record<OverrideFieldKey, boolean>;
  promptSections: Record<keyof AiPromptSections, boolean>;
}

/** 全 OFF (= 完全継承) の初期状態。 */
export function emptyOverrideState(): DistrictOverrideState {
  const fields = {} as Record<OverrideFieldKey, boolean>;
  for (const k of OVERRIDE_FIELD_KEYS) fields[k] = false;
  const promptSections = {} as Record<keyof AiPromptSections, boolean>;
  for (const k of AI_PROMPT_SECTION_KEYS) promptSections[k] = false;
  return { fields, promptSections };
}

/**
 * 既存の DistrictAiAnalysisConfig から override ON 状態を復元する (初期 state)。
 * 各 override フィールドが存在する (undefined でない) = その項目を地区で上書き中。
 * promptSectionOverrides は rules を除く各セクションの存在で判定 (rules はマスタ専管=常に OFF)。
 */
export function overrideStateFromConfig(
  config: DistrictAiAnalysisConfig | undefined | null,
): DistrictOverrideState {
  const state = emptyOverrideState();
  if (config == null) return state;
  state.fields.outputFormatId = config.outputFormatIdOverride !== undefined;
  state.fields.inputFieldKeys = config.inputFieldKeys !== undefined;
  state.fields.categoryMasterIds = config.categoryMasterIdsOverride !== undefined;
  state.fields.generationConfig = config.generationConfigOverride !== undefined;
  state.fields.titleGeneration = config.titleGenerationOverride !== undefined;
  state.fields.validators = config.validatorsOverride !== undefined;
  state.fields.outputFields = config.outputFieldsOverride !== undefined;
  const ovSec = config.promptSectionOverrides;
  if (ovSec != null) {
    for (const key of AI_PROMPT_SECTION_KEYS) {
      if (key === 'rules') continue; // rules はマスタ専管 (override 不可)
      state.promptSections[key] = ovSec[key] !== undefined;
    }
  }
  return state;
}

/**
 * buildDistrictConfigPayload に渡す draft の構造的サブセット (ProfileDraft の必要 fields のみ)。
 * ProfileDraft 本体への結合を避け、helper を unit test 可能にする。
 */
export interface DistrictConfigDraftSlice {
  outputFormatId: string;
  inputFieldKeys: string[] | undefined;
  categoryMasterIds: string[];
  generationConfig: AiGenerationConfig | null;
  titleGeneration: AiTitleGenerationConfig | null;
  validatorRules: AiValidatorRule[];
  promptSections: AiPromptSections;
  /**
   * プロンプト変数マッピング (要件3/5、UI が inputFieldKeys 選択に連動して autoPromptInputMapping
   * で自動補完する)。inputFieldKeys override に slave して promptInputMappingOverride へ保存される。
   */
  promptInputMapping: AiPromptInputMapping[];
}

export interface BuildDistrictConfigArgs {
  /** AI 解析の opt-in (必須)。 */
  enabled: boolean;
  /** 参照テンプレ M1 id (必須、null=自動選択)。 */
  settingsId: string | null;
  draft: DistrictConfigDraftSlice;
  /** 出力フォーマット fields[] override (outputFields トグル ON 時のみ採用、未編集なら null)。 */
  outputFieldsDraft: AiOutputFieldDef[] | null;
  overrides: DistrictOverrideState;
}

/**
 * override ON の項目だけを詰めた DistrictAiAnalysisConfig を組み立てる (Firestore 保存形)。
 *
 *  - enabled / settingsId は必須 (常に書く)。
 *  - OFF の項目はキー自体を書かない (= テンプレ継承へ戻る)。
 *  - undefined を一切含めない (Firestore は undefined を拒否する。commit 079aa32 同種の罠)。
 *  - rules は地区 override 不可 (マスタ専管) なので決して書かない。
 *  - validators は draft.validatorRules → `{ rules }` 形 (AiValidatorsConfig) に詰める。
 *  - titleGeneration は ON でも draft が null のときは「タイトル生成なし」を意図するため
 *    `{ enabled: false, instructions: '' }` を書く (undefined は書かない)。
 *
 * 注: 呼出側は merge:true ではなく aiAnalysis 全体置換で保存する想定なので、OFF=キー省略で
 * 継承へ確実に戻る (OFF→ON→OFF で残骸が残らない)。
 */
export function buildDistrictConfigPayload(
  args: BuildDistrictConfigArgs,
): DistrictAiAnalysisConfig {
  const { enabled, settingsId, draft, outputFieldsDraft, overrides } = args;
  const config: DistrictAiAnalysisConfig = { enabled, settingsId };

  if (overrides.fields.outputFormatId && draft.outputFormatId.trim()) {
    config.outputFormatIdOverride = draft.outputFormatId.trim();
  }
  if (overrides.fields.inputFieldKeys && draft.inputFieldKeys !== undefined) {
    config.inputFieldKeys = [...draft.inputFieldKeys];
    // promptInputMapping は inputFieldKeys 選択に slave (要件4/5: key 手入力なし・自動補完)。
    // inputFieldKeys を地区で上書きするときは、対応する変数マッピングも override として
    // 永続化しないと「テンプレ未マップの postField を地区で追加選択」が実効にならない
    // (analyzePost は effective promptInputMapping ∩ inputFieldKeys で送信フィールドを決めるため)。
    if (draft.promptInputMapping.length > 0) {
      config.promptInputMappingOverride = draft.promptInputMapping.map((m) => ({ ...m }));
    }
  }
  if (overrides.fields.categoryMasterIds) {
    config.categoryMasterIdsOverride = [...draft.categoryMasterIds];
  }
  if (overrides.fields.generationConfig && draft.generationConfig) {
    config.generationConfigOverride = { ...draft.generationConfig };
  }
  if (overrides.fields.titleGeneration) {
    config.titleGenerationOverride = draft.titleGeneration
      ? { ...draft.titleGeneration }
      : { enabled: false, instructions: '' };
  }
  if (overrides.fields.validators) {
    config.validatorsOverride = { rules: draft.validatorRules.map((r) => ({ ...r })) };
  }
  if (overrides.fields.outputFields && outputFieldsDraft != null) {
    config.outputFieldsOverride = outputFieldsDraft.map((f) => ({ ...f }));
  }

  // promptSections: ON のセクションだけを詰める (rules は決して書かない)。
  const sectionEntries: AiPromptSections = {};
  let anySection = false;
  for (const key of AI_PROMPT_SECTION_KEYS) {
    if (key === 'rules') continue;
    if (overrides.promptSections[key]) {
      sectionEntries[key] = draft.promptSections[key] ?? '';
      anySection = true;
    }
  }
  if (anySection) {
    config.promptSectionOverrides = sectionEntries;
  }

  return config;
}

/** thinkingConfig.thinkingLevel に保存を許す値 (M1 generationConfig、shared-types と一致)。 */
export const ALLOWED_THINKING_LEVELS = ['minimal', 'low', 'medium', 'high'] as const;

/**
 * generationConfig の数値項目を世代に応じて検証する。
 *
 * 温度 / Top-P / Top-K は第2世代でしか使わない (第3世代では画面にも出さず保存もしない)
 * ため、第3世代の設定ではこれらを必須扱いしない。最大出力トークン数は世代共通で必須。
 */
export function validateGenerationConfigNumbers(
  gc: AiGenerationConfig,
): { ok: true } | { ok: false; error: string } {
  const generation = resolveAiModelGeneration(gc);
  const fields: { key: string; value: number | undefined }[] = [];
  if (aiGenerationSupportsSampling(generation)) {
    fields.push(
      { key: 'temperature', value: gc.temperature },
      { key: 'topP', value: gc.topP },
      { key: 'topK', value: gc.topK },
    );
  }
  fields.push({ key: 'maxOutputTokens', value: gc.maxOutputTokens });
  for (const f of fields) {
    if (!Number.isFinite(f.value)) {
      return { ok: false, error: `generationConfig.${f.key} は数値で入力してください` };
    }
  }
  return { ok: true };
}

/**
 * generationConfig のうち数値以外 (model / thinkingConfig / generation) を検証して正規化する。
 *
 * - `model`: 自由入力を許すため必須ではないが、空白のみの文字列は保存しない (キーごと落とす)。
 * - `thinkingConfig.thinkingLevel`: ALLOWED_THINKING_LEVELS 以外はエラー。未設定なら
 *   `thinkingConfig` ごと落として空オブジェクトを Firestore に残さない。
 * - 世代: 保存値が無ければモデル名から推定して `generation` を明示的に立て、その世代で
 *   使わない項目 (第3世代の temperature / topP / topK / thinkingBudget、第2世代の
 *   thinkingLevel) を落とす — 画面で扱わない値を Firestore に残して混乱させないため。
 *
 * 数値項目は `validateGenerationConfigNumbers` で先に検証する。
 */
export function validateGenerationConfigMeta(
  gc: AiGenerationConfig,
): { ok: true; value: AiGenerationConfig } | { ok: false; error: string } {
  const next: AiGenerationConfig = { ...gc };

  if (typeof next.model === 'string') {
    const trimmed = next.model.trim();
    if (trimmed) {
      next.model = trimmed;
    } else {
      delete next.model;
    }
  } else if (next.model != null) {
    return { ok: false, error: 'モデル名 (model) は文字列で入力してください' };
  }

  const level = next.thinkingConfig?.thinkingLevel;
  if (level != null && !ALLOWED_THINKING_LEVELS.includes(level)) {
    return {
      ok: false,
      error: `推論レベル (thinkingLevel) は ${ALLOWED_THINKING_LEVELS.join(' / ')} のいずれかを指定してください`,
    };
  }
  if (next.thinkingConfig) {
    const cleaned: NonNullable<AiGenerationConfig['thinkingConfig']> = {};
    if (level != null) cleaned.thinkingLevel = level;
    if (typeof next.thinkingConfig.thinkingBudget === 'number') {
      cleaned.thinkingBudget = next.thinkingConfig.thinkingBudget;
    }
    if (Object.keys(cleaned).length > 0) {
      next.thinkingConfig = cleaned;
    } else {
      delete next.thinkingConfig;
    }
  }

  // 世代を確定させ (未設定の既存データはモデル名から推定)、使わない項目を落として保存する。
  return { ok: true, value: applyAiModelGeneration(next, resolveAiModelGeneration(next)) };
}
